// Sung vowels from measured human recordings, ported from Bop 4's Rust engine
// (crates/bop-dsp/src/vocal.rs) so the classic editor sings with the same
// voices. Harmonic amplitudes come from fixed-frequency envelopes, so formants
// stay put as pitch changes; phases come from a glottal pulse. Aspiration noise
// uses the measured inter-harmonic floor, pulsed with the glottal cycle.
import {inverseRealFourierTransform} from "./FFT.js";
import {vocalModel} from "./VocalData.js";

const LENGTH = 1024;
const NOISE_LENGTH = 1 << 16;
/** Tables either side of the note pitch, so vibrato moves harmonics through
 * the fixed formants instead of dragging the formants along with pitch. */
const SPREAD_SEMITONES = 1;
const CEILING_HZ = 10000;
/** Calibrates the measured breath against the development singers (see vocal.rs). */
const BREATH_CALIBRATION = 0.4;

export type Register = "low" | "high";
export type Vowel = "ah" | "oo";
/** Acoustic modes for the four measured voices, after the spectral modes. */
export const vocalModes: ReadonlyArray<{name: string, register: Register, vowel: Vowel}> = [
	{name: "vocal-low-ah", register: "low", vowel: "ah"},
	{name: "vocal-low-oo", register: "low", vowel: "oo"},
	{name: "vocal-high-ah", register: "high", vowel: "ah"},
	{name: "vocal-high-oo", register: "high", vowel: "oo"},
];

/** Measured vibrato (rate, extent) and slow drift for a register. */
export function vocalExpression(register: Register): {rateHz: number, extentCents: number, driftCents: number} {
	return vocalModel.voices[register].vibrato;
}

function envelopeAt(envelope: number[], hz: number): number {
	const grid = vocalModel.grid;
	if (hz <= grid[0]) return envelope[0];
	let i = 0;
	while (i < grid.length && grid[i] < hz) i++;
	if (i >= grid.length) return envelope[grid.length - 1];
	const t = (Math.log(hz) - Math.log(grid[i - 1])) / (Math.log(grid[i]) - Math.log(grid[i - 1]));
	return envelope[i - 1] + (envelope[i] - envelope[i - 1]) * t;
}

/** The envelope a singer would have at `midi`, interpolated in dB between the
 * measured notes of the same vowel and dynamic, clamped at the edges. */
function layerEnvelope(register: Register, vowel: Vowel, dynamic: number, midi: number, noise: boolean): number[] {
	const layers = vocalModel.voices[register].layers.filter(l => l.vowel == vowel && l.dynamic == dynamic).sort((a, b) => a.midi - b.midi);
	const pick = (l: typeof layers[number]) => (noise ? l.noiseDb : l.harmonicsDb).slice();
	let i = 0;
	while (i < layers.length && layers[i].midi < midi) i++;
	if (i == 0) return pick(layers[0]);
	if (i == layers.length) return pick(layers[i - 1]);
	const a = layers[i - 1], b = layers[i];
	const t = (midi - a.midi) / (b.midi - a.midi);
	const x = pick(a), y = pick(b);
	return x.map((v, k) => v + (y[k] - v) * t);
}

/** Rosenberg glottal flow derivative phases (magnitudes unused). */
let glottalPhases: Float64Array | null = null;
function glottal(): Float64Array {
	if (glottalPhases != null) return glottalPhases;
	const open = 0.4, closing = 0.16;
	const flow = (t: number) => t < open ? 0.5 * (1 - Math.cos(Math.PI * t / open)) : t < open + closing ? Math.cos(Math.PI * (t - open) / (2 * closing)) : 0;
	const wrap = (t: number) => ((t % 1) + 1) % 1;
	const n = LENGTH;
	const derivative = new Float64Array(n);
	for (let i = 0; i < n; i++) derivative[i] = (flow(wrap(i / n + 0.5 / n)) - flow(wrap(i / n - 0.5 / n))) * n;
	glottalPhases = new Float64Array(LENGTH / 2 - 1);
	for (let k = 1; k < LENGTH / 2; k++) {
		let re = 0, im = 0;
		for (let i = 0; i < n; i++) {
			const angle = Math.PI * 2 * k * i / n;
			re += derivative[i] * Math.cos(angle);
			im -= derivative[i] * Math.sin(angle);
		}
		glottalPhases[k - 1] = Math.atan2(im, re);
	}
	return glottalPhases;
}

/** Per-note tables at three pitches (±1 semitone) and two dynamics. */
export class VocalBank {
	public readonly waves: Float32Array[][];
	constructor(register: Register, vowel: Vowel, midi: number, public readonly rate: number, ceilingHz: number) {
		const baseHz = 440 * Math.pow(2, (midi - 69) / 12);
		this.baseHz = baseHz;
		const headroom = Math.max(1, ceilingHz / baseHz);
		const phases = glottal();
		this.waves = [0, 1].map(dynamic => {
			const envelope = layerEnvelope(register, vowel, dynamic, midi, false);
			return [0, 1, 2].map(step => {
				const hz = baseHz * Math.pow(2, (step - 1) * SPREAD_SEMITONES / 12);
				const data = new Float32Array(LENGTH);
				let energy = 0;
				for (let k = 1; k < LENGTH / 2; k++) {
					const f = hz * k, highest = f * headroom;
					if (f > CEILING_HZ || highest > rate * 0.46) break;
					const taper = Math.max(0, Math.min(1, (rate * 0.46 - highest) / (rate * 0.06)));
					const amplitude = Math.pow(10, envelopeAt(envelope, f) / 20) * taper;
					data[k] = amplitude * Math.cos(phases[k - 1]);
					data[LENGTH - k] = amplitude * Math.sin(phases[k - 1]);
					energy += amplitude * amplitude;
				}
				inverseRealFourierTransform(data, LENGTH);
				let sum = 0;
				for (let i = 0; i < LENGTH; i++) sum += data[i] * data[i];
				const rms = Math.sqrt(sum / LENGTH);
				// Unit RMS per table: loudness belongs to note size; dynamics
				// change only spectral balance.
				const scale = rms > 0 && energy > 0 ? 1 / rms : 0;
				const wave = new Float32Array(LENGTH + 1);
				for (let i = 0; i < LENGTH; i++) wave[i] = data[i] * scale;
				wave[LENGTH] = wave[0];
				return wave;
			});
		});
	}
	public readonly baseHz: number;
}

/** Aspiration noise with the measured spectrum, shared by an instrument's notes. */
export class VocalBreath {
	public readonly tables: Float32Array[];
	constructor(register: Register, vowel: Vowel, rate: number, seed: number) {
		let random = Math.max(1, seed >>> 0);
		const midis = vocalModel.voices[register].layers.map(l => l.midi).sort((a, b) => a - b);
		this.tables = [0, 1].map(dynamic => {
			// Aspiration shape changes little with pitch; use the middle note.
			const envelope = layerEnvelope(register, vowel, dynamic, midis[midis.length >> 1], true);
			const data = new Float32Array(NOISE_LENGTH);
			const bin = rate / NOISE_LENGTH;
			let power = 0;
			for (let k = 1; k < NOISE_LENGTH / 2; k++) {
				const f = k * bin;
				if (f > Math.min(CEILING_HZ, rate * 0.45)) break;
				const density = Math.pow(10, envelopeAt(envelope, f) / 10) * 2;
				const amplitude = Math.sqrt(density * bin);
				random ^= random << 13; random >>>= 0;
				random ^= random >>> 17;
				random ^= random << 5; random >>>= 0;
				const phase = random / 0xffffffff * Math.PI * 2;
				data[k] = amplitude * Math.cos(phase);
				data[NOISE_LENGTH - k] = amplitude * Math.sin(phase);
				power += density * bin;
			}
			inverseRealFourierTransform(data, NOISE_LENGTH);
			let sum = 0;
			for (let i = 0; i < NOISE_LENGTH; i++) sum += data[i] * data[i];
			const rms = Math.sqrt(sum / NOISE_LENGTH);
			const scale = rms > 0 ? Math.sqrt(power) / rms : 0;
			for (let i = 0; i < NOISE_LENGTH; i++) data[i] *= scale;
			return data;
		});
	}
	private static readonly _cache: Map<string, VocalBreath> = new Map();
	/** Breath tables are large; share them per voice and sample rate. */
	public static shared(register: Register, vowel: Vowel, rate: number): VocalBreath {
		const key = register + vowel + rate;
		let breath = VocalBreath._cache.get(key);
		if (breath == undefined) {
			breath = new VocalBreath(register, vowel, rate, 0x9e3779b9);
			VocalBreath._cache.set(key, breath);
		}
		return breath;
	}
}

function lookup(wave: Float32Array, phase: number): number {
	const x = (((phase % 1) + 1) % 1) * LENGTH;
	const i = Math.min(x | 0, LENGTH - 1);
	return wave[i] + (wave[i + 1] - wave[i]) * (x - i);
}

export class VocalVoice {
	private phase: number;
	private noisePosition: number;
	private seed: number;
	private drift: number = 0;
	private driftTarget: number = 0;
	private driftCountdown: number = 0;
	private readonly driftRate: number;
	private readonly driftInterval: number;
	private readonly driftDepth: number;
	constructor(private readonly bank: VocalBank, private readonly breath: VocalBreath, seed: number, driftCents: number) {
		let state = Math.max(1, seed >>> 0);
		const next = () => { state ^= state << 13; state >>>= 0; state ^= state >>> 17; state ^= state << 5; state >>>= 0; return state; };
		this.phase = next() / 0xffffffff;
		this.noisePosition = next() % NOISE_LENGTH;
		this.seed = Math.max(1, next());
		// A new target every ~0.4 s, approached with a ~0.6 s lag: slow wander.
		this.driftRate = 1 - Math.exp(-Math.PI * 2 * 0.25 / bank.rate);
		this.driftInterval = (bank.rate * 0.4) | 0;
		this.driftDepth = driftCents / 1200;
	}
	/** `hz` includes bends and vibrato; `dynamic` is 0..1. */
	public process(hz: number, dynamic: number): number {
		if (!(hz > 0) || !isFinite(dynamic)) return 0;
		if (this.driftCountdown == 0) {
			this.driftCountdown = this.driftInterval;
			let x = this.seed;
			x ^= x << 13; x >>>= 0; x ^= x >>> 17; x ^= x << 5; x >>>= 0;
			this.seed = x;
			this.driftTarget = (x / 0xffffffff * 2 - 1) * 1.7 * this.driftDepth;
		}
		this.driftCountdown--;
		this.drift += this.driftRate * (this.driftTarget - this.drift);
		const bank = this.bank;
		hz *= Math.pow(2, this.drift);
		this.phase = (this.phase + hz / bank.rate) % 1;
		const position = Math.max(-1, Math.min(1, Math.log2(hz / bank.baseHz) * 12 / SPREAD_SEMITONES)) + 1;
		const step = position >= 1 ? 1 : 0, t = position >= 1 ? position - 1 : position;
		const d = Math.max(0, Math.min(1, dynamic));
		let voiced = 0;
		for (const [index, weight] of [[0, 1 - d], [1, d]]) {
			const a = lookup(bank.waves[index][step], this.phase);
			const b = lookup(bank.waves[index][step + 1], this.phase);
			voiced += (a + (b - a) * t) * weight;
		}
		// Aspiration is strongest while the glottis is open.
		const open = Math.max(0, Math.sin(Math.PI * 2 * this.phase));
		const noise = this.breath.tables[0][this.noisePosition] * (1 - d) + this.breath.tables[1][this.noisePosition] * d;
		this.noisePosition = (this.noisePosition + 1) % NOISE_LENGTH;
		return voiced + noise * (0.5 + open) * 1.105 * BREATH_CALIBRATION;
	}
}

const banks = new Map<string, VocalBank>();
function bankFor(register: Register, vowel: Vowel, midi: number, rate: number, ceilingHz: number): VocalBank {
	const key = [register, vowel, midi, rate, Math.round(ceilingHz)].join(":");
	let bank = banks.get(key);
	if (bank == undefined) {
		bank = new VocalBank(register, vowel, midi, rate, ceilingHz);
		banks.set(key, bank);
		// Bounded shared cache; sounding notes keep their bank after eviction.
		if (banks.size > 48) banks.delete(banks.keys().next().value!);
	}
	return bank;
}

/** Unit-RMS tables scaled so the sung voices sit against the classic spectral
 * and choir voices as they do in Bop 4 (one sustained note at full size,
 * where violin, cello, flute and two choirs agree between engines within 0.6 dB). */
export const vocalGain = 3.2;

/** A tone's sung voice: the bank is chosen once at note start, like Bop 4,
 * then the song's pitch (bends and vibrato included) plays through it. */
export class SungVoice {
	private voice: VocalVoice | null = null;
	private seed: number = 1;
	public reset(seed: number = 1): void {
		this.voice = null;
		this.seed = (seed >>> 0) || 1;
	}
	/** `mode` indexes `vocalModes`; `ceilingHz` is the highest pitch the note
	 * should reach, so harmonics taper before they alias. */
	public configure(mode: number, rate: number, frequency: number, ceilingHz: number): void {
		if (this.voice != null) return;
		const {register, vowel} = vocalModes[mode];
		const midi = Math.max(0, Math.min(127, Math.round(69 + 12 * Math.log2(frequency / 440))));
		const bank = bankFor(register, vowel, midi, rate, Math.max(frequency, ceilingHz));
		this.voice = new VocalVoice(bank, VocalBreath.shared(register, vowel, rate), this.seed, vocalExpression(register).driftCents);
	}
	/** `size` is the classic note size, 0..3. */
	public process(hz: number, size: number): number {
		return this.voice == null ? 0 : this.voice.process(hz, size / 3) * vocalGain;
	}
}

/** Builds the shared glottal phases and breath tables (about 17 ms) before
 * playback, so the first sung note doesn't stall the audio callback. */
export function prepareSungVoice(mode: number, rate: number): void {
	const {register, vowel} = vocalModes[mode];
	glottal();
	VocalBreath.shared(register, vowel, rate);
}
